import React, { useEffect, useState } from 'react';
import { X, Calendar, User, Clock } from 'lucide-react';

interface NewsContent {
  type: 'paragraph' | 'heading';
  text: string;
}

interface NewsArticle {
  id: number;
  title: string;
  subtitle: string;
  image: string;
  category: string;
  date: string;
  author: string;
  readTime: string;
  content: NewsContent[];
  tags: string[];
}

interface NewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  newsId: number | null;
  sourceData: string | null;
}

const NewsModal: React.FC<NewsModalProps> = ({ isOpen, onClose, newsId, sourceData }) => {
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && sourceData && newsId) {
      loadArticle();
    }
  }, [isOpen, sourceData, newsId]);

  const loadArticle = async () => {
    if (!sourceData) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/${sourceData}`);
      const data = await response.json();
      setArticle(data);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 news-modal-backdrop"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden news-modal-content">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all duration-200"
        >
          <X size={24} className="text-gray-600" />
        </button>

        {/* Content */}
        <div className="overflow-y-auto max-h-[90vh] news-modal-scroll">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : article ? (
            <>
              {/* Header Image */}
              <div className="relative h-64 md:h-80">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-block px-3 py-1 bg-blue-600 text-white text-sm rounded-full mb-2">
                    {article.category}
                  </span>
                  <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    {article.title}
                  </h1>
                  <p className="text-gray-200 text-lg">
                    {article.subtitle}
                  </p>
                </div>
              </div>

              {/* Article Meta */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Calendar size={16} />
                    <span>{article.date}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User size={16} />
                    <span>{article.author}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={16} />
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </div>

              {/* Article Content */}
              <div className="p-6">
                <div className="prose prose-lg max-w-none">
                  {article.content.map((item, index) => (
                    <div key={index} className="mb-4">
                      {item.type === 'heading' ? (
                        <h2 className="text-2xl font-bold text-gray-900 mb-3 mt-6">
                          {item.text}
                        </h2>
                      ) : (
                        <p className="text-gray-700 leading-relaxed text-lg">
                          {item.text}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">العلامات</h3>
                    <div className="flex flex-wrap gap-2">
                      {article.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="p-6 text-center">
              <p className="text-gray-600">فشل في تحميل المقال</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsModal;